import dunegql from "src/utils/dunegqlCodegen/dunegql";
import {
  QueryAlertsResult,
  useDeleteQueryAlertMutation,
} from "src/gql/codegen/duneGraphql";

import type { SessionWithUser } from "src/utils/users/types";

import * as Sentry from "@sentry/react";
import { isNullable, isNonNullable } from "src/utils/types/types";
import { assertUnreachable } from "src/utils/assertUnreachable";

type AlertType = keyof Required<Omit<QueryAlertsResult, "__typename">>;

export function useDeleteQueryAlert() {
  const [callDeleteQueryAlert, deleteQueryAlertResult] =
    useDeleteQueryAlertMutation();

  function deleteQueryAlert(
    session: SessionWithUser,
    alertId: string,
    alertType: AlertType
  ): Promise<void> {
    if (isNullable(session) || deleteQueryAlertResult.loading) {
      return new Promise(() => {});
    }

    return new Promise((resolve) => {
      callDeleteQueryAlert({
        context: { session },
        variables: {
          id: alertId,
        },
        update: (cache, { data }) => {
          if (isNonNullable(data) && data.deleteAlert.ok) {
            let typename: string;
            switch (alertType) {
              case "email":
                typename = "QueryEmailAlert";
                break;
              case "slack":
                typename = "QuerySlackAlert";
                break;
              case "webhook":
                typename = "QueryWebhookAlert";
                break;
              default:
                assertUnreachable(alertType);
            }
            cache.evict({
              id: cache.identify({
                __typename: typename,
                id: alertId,
              }),
            });
            cache.gc();
          }
        },
      }).then(
        () => {
          resolve();
        },
        (error) => {
          Sentry.captureException(
            `Failed to delete ${alertType} query alert: ${error.message}`
          );
        }
      );
    });
  }

  return [deleteQueryAlert, deleteQueryAlertResult] as const;
}

dunegql`
  mutation DeleteQueryAlert($id: String!) {
    deleteAlert(id: $id) {
      ok
    }
  }
`;
